import { z } from 'zod';

import type { TreatmentApproach } from '@nesso/common-contracts/treatment-approaches';

const aedpSchema = z.object({
    treatmentApproach: z.literal('AEDP'),
    interventions: z
        .array(
            z.enum([
                'Build up resilience and improve functioning',
                'Communicate about difficult emotions',
                'Feel a sense of relief',
                'Client feels safe to experience traumatic memories',
                'Reflect on their experience',
                'Soften defenses',
            ]),
        )
        .optional(),
    goals: z
        .array(
            z.enum([
                'Facilitate secure attachment within the therapeutic relationship',
                'Support deep emotional processing and transformation',
                'Strengthen resilience and integration of adaptive emotional states',
            ]),
        )
        .optional(),
    otherGoal: z.string().trim().optional(),
});

const actSchema = z.object({
    treatmentApproach: z.literal('ACT'),
    interventions: z
        .array(
            z.enum([
                'Facing the current situation',
                'Acceptance',
                'Cognitive defusion (Deliteralization)',
                'Valuing as a choice',
                'Self as context',
                'Being present',
            ]),
        )
        .optional(),
    goals: z
        .array(
            z.enum([
                'Increase psychological flexibility',
                'Reduce experiential avoidance',
                'Promote values-driven behavior and acceptance of internal experiences',
            ]),
        )
        .optional(),
    otherGoal: z.string().trim().optional(),
});

const abtSchema = z.object({
    treatmentApproach: z.literal('ATTACHMENT_BASED'),
    interventions: z
        .array(
            z.enum([
                'Establish secure bond with counselor',
                'Explore abandonment styles',
                'Explore attachment styles',
                'Explore Fight, Flight, Freeze responses',
                'Gain increased awareness',
                'Taking back power',
            ]),
        )
        .optional(),
    goals: z
        .array(
            z.enum([
                'Repair attachment disruptions',
                'Strengthen secure relational patterns',
                'Improve emotional regulation and interpersonal trust',
            ]),
        )
        .optional(),
    otherGoal: z.string().trim().optional(),
});

const bftSchema = z.object({
    treatmentApproach: z.literal('BOWEN_FAMILY'),
    interventions: z
        .array(
            z.enum([
                'Detriangulation',
                'Differentiation of Self',
                'Emotional Cut Off',
                'Express anger without yelling',
                'Express feelings verbally without acting out',
                'Genograms',
                'Learn ways to communicate effectively',
                'Multigenerational transmission process',
                'Nuclear family emotional process',
                'Recognize patterns of conflict',
                'Sibling Positioning',
                'Societal Emotional Processes',
                'Triangulation',
            ]),
        )
        .optional(),
    goals: z
        .array(
            z.enum([
                'Increase differentiation of self',
                'Reduce emotional reactivity within the family system',
                'Interrupt intergenerational patterns of dysfunction',
            ]),
        )
        .optional(),
    otherGoal: z.string().trim().optional(),
});

const cbtSchema = z.object({
    treatmentApproach: z.literal('CBT'),
    interventions: z
        .array(
            z.enum([
                'Cognitive restructuring',
                'Goal-setting',
                'Graded exposure',
                'Identifying negative thoughts',
                'Practicing new skills',
                'Problem solving',
                'Self monitoring',
                'Socratic questioning',
                'Successive approximation',
            ]),
        )
        .optional(),
    goals: z
        .array(
            z.enum([
                'Identify and modify maladaptive thoughts and behaviors',
                'Improve coping and problem-solving skills',
                'Enhance emotional regulation through cognitive restructuring',
            ]),
        )
        .optional(),
    otherGoal: z.string().trim().optional(),
});

const cptSchema = z.object({
    treatmentApproach: z.literal('CPT'),
    interventions: z
        .array(
            z.enum([
                'Identifying trauma themes',
                'Psychoeducation on trauma',
                'Socratic questioning: analyzing implications and consequences',
                'Socratic questioning: clarification',
                'Socratic questioning: probing assumptions',
                'Socratic questioning: probing reasons and evidence',
                'Socratic questioning: questions about the question',
                'Socratic questioning: questioning viewpoints and perspectives',
                'Trauma processing',
            ]),
        )
        .optional(),
    goals: z
        .array(
            z.enum([
                'Reduce trauma-related cognitive distortions',
                'Promote balanced and adaptive beliefs about traumatic events',
                'Decrease trauma-related distress and avoidance',
            ]),
        )
        .optional(),
    otherGoal: z.string().trim().optional(),
});

const craftSchema = z.object({
    treatmentApproach: z.literal('CRAFT'),
    interventions: z
        .array(
            z.enum([
                'Communication skills training',
                "Enhancing the CSO's happiness",
                'Functional analysis',
                "Functional analysis of IP's substance use",
                'Interpersonal violence precautions',
                'Motivational induction',
                'Natural consequences',
                'Problem-solving',
                'Suggestion of treatment to the IP',
                'Use of positive reinforcement',
            ]),
        )
        .optional(),
    goals: z
        .array(
            z.enum([
                'Support behavior change in individuals with substance use issues',
                'Strengthen positive communication and reinforcement strategies',
                'Reduce enabling behaviors and increase safety',
            ]),
        )
        .optional(),
    otherGoal: z.string().trim().optional(),
});

const cftSchema = z.object({
    treatmentApproach: z.literal('CFT'),
    interventions: z
        .array(
            z.enum([
                'Appreciation exercises',
                'Compassionate colors',
                'Compassionate flowing into oneself using memory',
                'Compassionate flowing out',
                'Creating a compassionate ideal',
                'Creating a safe place',
                'Focusing the compassionate self on others',
                'Focusing the compassionate self on yourself',
                'Guided imagery exercises',
                'Mindfulness',
                'Simple body scan and relaxation',
                'Soothing rhythm breathing',
            ]),
        )
        .optional(),
    goals: z
        .array(
            z.enum([
                'Reduce shame and self-criticism',
                'Strengthen self-compassion and emotional soothing systems',
                'Improve emotional regulation and resilience',
            ]),
        )
        .optional(),
    otherGoal: z.string().trim().optional(),
});

const ctSchema = z.object({
    treatmentApproach: z.literal('CREATIVE'),
    interventions: z
        .array(
            z.enum([
                'Coloring',
                'Collage',
                'Dance/Movement',
                'Expressive arts',
                'Music',
                'Painting',
                'Photography',
                'Play',
                'Sand play',
                'Sculpting',
                'Writing',
            ]),
        )
        .optional(),
    goals: z
        .array(
            z.enum([
                'Facilitate emotional expression through creative modalities',
                'Increase self-awareness and symbolic processing',
                'Support healing through experiential exploration',
            ]),
        )
        .optional(),
    otherGoal: z.string().trim().optional(),
});

const cstSchema = z.object({
    treatmentApproach: z.literal('CULTURALLY_SENSITIVE'),
    interventions: z
        .array(
            z.enum([
                'Being aware of own culture and values',
                'Building a cross-cultural care team',
                'Developing culturally appropriate interventions',
                'Processing institutional barriers',
                'Respecting differences',
                "Understanding your client's worldview",
            ]),
        )
        .optional(),
    goals: z
        .array(
            z.enum([
                'Integrate cultural identity and values into treatment',
                'Reduce cultural barriers to engagement',
                'Promote culturally congruent coping and meaning-making',
            ]),
        )
        .optional(),
    otherGoal: z.string().trim().optional(),
});

const dbtSchema = z.object({
    treatmentApproach: z.literal('DBT'),
    interventions: z
        .array(
            z.enum([
                'Acceptance and change',
                'Addressing lifestyle issues',
                'Distress tolerance',
                'Emotional regulation practice',
                'Emotional regulation psychoeducation',
                'Functional analysis',
                'Interpersonal effectiveness',
                'Mindfulness',
                'Mindfulness: engaging in life',
                'Mindfulness: improving emotional regulation',
                'Mindfulness: increasing concentration and memory',
                'Mindfulness: relaxation',
                'Mindfulness: taking control',
                'Reframing',
                'Simply observing thoughts',
            ]),
        )
        .optional(),
    goals: z
        .array(
            z.enum([
                'Build skills in mindfulness, distress tolerance, emotion regulation, and interpersonal effectiveness',
                'Reduce self-destructive or impulsive behaviors',
                'Increase emotional and relational stability',
            ]),
        )
        .optional(),
    otherGoal: z.string().trim().optional(),
});

const eftSchema = z.object({
    treatmentApproach: z.literal('EFT'),
    interventions: z
        .array(
            z.enum([
                'Become more aware of emotions',
                'Develop alternative healthy coping strategies',
                'Evaluate if emotions are helpful or unhelpful',
                'Form personal scripts to challenge maladaptive emotions',
                'Identify maladaptive patterns and learn techniques to heal',
                'Increase awareness of multiple layers of emotional experience',
                'Learn to change unhelpful emotions',
                'Learn to describe emotions clearly',
                'Learn to use helpful emotions to guide actions',
                'Learn to welcome, allow, and regulate emotions',
            ]),
        )
        .optional(),
    goals: z
        .array(
            z.enum([
                'Restructure negative interaction cycles',
                'Strengthen emotional bonds and attachment security',
                'Increase emotional responsiveness and connection',
            ]),
        )
        .optional(),
    otherGoal: z.string().trim().optional(),
});

const extSchema = z.object({
    treatmentApproach: z.literal('EXPERIENTIAL'),
    interventions: z
        .array(
            z.enum([
                'Animal care',
                'Arts and crafts',
                'Empty Chair',
                'Family Sculpt',
                'Games',
                'Guided Imagery',
                'Music',
                'Role play',
                'Psychodrama',
            ]),
        )
        .optional(),
    goals: z
        .array(
            z.enum([
                'Access and process emotions through action-oriented experiences',
                'Increase authenticity and emotional awareness',
                'Resolve internal conflicts through lived experience',
            ]),
        )
        .optional(),
    otherGoal: z.string().trim().optional(),
});

const erpSchema = z.object({
    treatmentApproach: z.literal('ERP'),
    interventions: z
        .array(
            z.enum([
                'Cognitive restructuring',
                'Exposure to fear',
                'Flooding',
                'Graded exposure',
                'Imaginal exposure',
                'In-vivo exposure',
                'Prolonged exposure',
                'Systematic desensitization',
                'Virtual reality exposure',
            ]),
        )
        .optional(),
    goals: z
        .array(
            z.enum([
                'Reduce avoidance and compulsive behaviors',
                'Increase tolerance for anxiety and uncertainty',
                'Promote habituation to feared stimuli',
            ]),
        )
        .optional(),
    otherGoal: z.string().trim().optional(),
});

const emdrSchema = z.object({
    treatmentApproach: z.literal('EMDR'),
    interventions: z
        .array(
            z.enum([
                'Auditory bilateral stimulation',
                'Eye movement with lightbar',
                'Eye movement manual',
                'Identifying clinical theme: power',
                'Identifying clinical theme: responsibility',
                'Identifying clinical theme: safety',
                'Identifying desired future outcomes',
                'Identifying external resources',
                'Identifying internal resources',
                'Identifying past experience with age',
                'Identifying present triggers',
                'Identifying target memory for reprocessing',
                'Re-evaluation of previous reprocessing session',
                'Tapping',
            ]),
        )
        .optional(),
    goals: z
        .array(
            z.enum([
                'Reduce distress associated with traumatic memories',
                'Facilitate adaptive reprocessing and integration',
                'Strengthen positive self-beliefs',
            ]),
        )
        .optional(),
    otherGoal: z.string().trim().optional(),
});

const imagoSchema = z.object({
    treatmentApproach: z.literal('IMAGO'),
    interventions: z
        .array(
            z.enum([
                'Behavior-change requests',
                'Exploring childhood experiences',
                'Imago Dialogue',
                'Relationship visioning',
                'Re-imaging the partner',
                'Re-romanticizing and caring behaviors',
            ]),
        )
        .optional(),
    goals: z
        .array(
            z.enum([
                'Improve empathic communication and mutual understanding between partners',
                'Reduce reactive conflict, blame, criticism, and harmful interaction patterns',
                'Strengthen emotional safety, trust, and secure connection within the relationship',
            ]),
        )
        .optional(),
    otherGoal: z.string().trim().optional(),
});

const ifsSchema = z.object({
    treatmentApproach: z.literal('IFS'),
    interventions: z
        .array(
            z.enum([
                'Creating safety plan for parts',
                'Expressing empathy for parts',
                'Identifying alliances',
                'Identifying polarization',
                'Psychoeducation on core self',
                'Psychoeducation on exile',
                'Psychoeducation on firefighter',
                'Psychoeducation on manager',
                'Unblending parts',
                'Unburdening parts',
            ]),
        )
        .optional(),
    goals: z
        .array(
            z.enum([
                'Heal wounded internal parts',
                'Increase harmony among internal subpersonalities',
                'Strengthen the Self as a compassionate internal leader',
            ]),
        )
        .optional(),
    otherGoal: z.string().trim().optional(),
});

const iptSchema = z.object({
    treatmentApproach: z.literal('IPT'),
    interventions: z
        .array(
            z.enum([
                'Closeness circle',
                'Drama techniques',
                'Guided imagery and scripting',
                'Interpersonal deficits',
                'Mindfulness and attention regulation',
                'Move the thermometer',
                'Practicing I-statements',
                'Restructuring attention',
                'Role dispute',
                'Role transition',
                'Breath awareness meditation',
                'Five senses worksheet',
                'Visualization',
            ]),
        )
        .optional(),
    goals: z
        .array(
            z.enum([
                'Improve communication and relational functioning',
                'Reduce distress linked to interpersonal conflict or role transitions',
                'Strengthen social support networks',
            ]),
        )
        .optional(),
    otherGoal: z.string().trim().optional(),
});

const istdpSchema = z.object({
    treatmentApproach: z.literal('ISTDP'),
    interventions: z
        .array(
            z.enum([
                'Applied Pressure to Affect',
                'Challenged Resistance',
                'Conducted Head-on Collision',
                'Facilitated Unlocking of the Unconscious',
                'Identified and Clarified Defenses',
                'Linked Triangle of Person (T-C-P)',
                'Monitored/Regulated Somatic Anxiety',
                'Mobilized Unconscious Therapeutic Alliance (UTA)',
                'Rendered Defenses Ego-Dystonic',
            ]),
        )
        .optional(),
    goals: z
        .array(
            z.enum([
                'Identify and process unconscious emotional conflicts',
                'Reduce reliance on maladaptive defenses',
                'Increase emotional freedom and insight',
            ]),
        )
        .optional(),
    otherGoal: z.string().trim().optional(),
});

const mbctSchema = z.object({
    treatmentApproach: z.literal('MBCT'),
    interventions: z
        .array(
            z.enum([
                '3-minute breathing space',
                '5 senses exercise',
                'Body scan',
                'Mindful breathing',
                'Mindful listening',
                'Mindful seeing',
                'Mindfulness showing',
                'Mindful stretching',
                'Yoga exercises',
            ]),
        )
        .optional(),
    goals: z
        .array(
            z.enum([
                'Reduce relapse of depressive symptoms',
                'Increase mindful awareness of thoughts and emotions',
                'Decrease automatic negative thinking patterns',
            ]),
        )
        .optional(),
    otherGoal: z.string().trim().optional(),
});

const miSchema = z.object({
    treatmentApproach: z.literal('MI'),
    interventions: z
        .array(
            z.enum([
                'Stages of change psychoeducation',
                'Autonomy over authority',
                'Building motivation for change (OARS)',
                'Collaboration versus confrontation',
                'Developing discrepancy',
                'Express empathy',
                'Identifying ambivalence',
                'Reflective listening',
                'Rolling with resistance',
                'Strengthening commitment to change (DARN)',
                'Supporting self-efficacy',
            ]),
        )
        .optional(),
    goals: z
        .array(
            z.enum([
                'Strengthen intrinsic motivation for change',
                'Resolve ambivalence',
                'Support autonomy and self-efficacy',
            ]),
        )
        .optional(),
    otherGoal: z.string().trim().optional(),
});

const ntSchema = z.object({
    treatmentApproach: z.literal('NARRATIVE'),
    interventions: z
        .array(
            z.enum([
                'Externalizing the problem-saturated story',
                "Finding one's own meaning",
                'Finding unique outcomes',
                'Identifying a problem and rewriting your own story',
                'Identifying the problem-saturated story',
                'Putting the story together',
            ]),
        )
        .optional(),
    goals: z
        .array(
            z.enum([
                'Externalize problems and re-author personal narratives',
                'Strengthen preferred identities and values',
                'Increase empowerment and agency',
            ]),
        )
        .optional(),
    otherGoal: z.string().trim().optional(),
});

const pcitSchema = z.object({
    treatmentApproach: z.literal('PCIT'),
    interventions: z
        .array(
            z.enum([
                'CDI: development',
                'CDI: differential',
                'CDI: positive affirmation',
                'CDI: play therapy skills',
                'PDI: clear communication',
                'PDI: consistency',
                'PDI: reasoning skills',
                'PRIDE skills',
            ]),
        )
        .optional(),
    goals: z
        .array(
            z.enum([
                'Improve parent-child attachment and communication',
                'Strengthen positive parenting skills',
                'Reduce disruptive child behaviors through structured interaction',
            ]),
        )
        .optional(),
    otherGoal: z.string().trim().optional(),
});

const pctSchema = z.object({
    treatmentApproach: z.literal('PERSON_CENTERED'),
    interventions: z
        .array(
            z.enum([
                'Congruence',
                'Eliminate or mitigate feelings of distress',
                'Empathetic understanding',
                "Enhance the client's understanding of self",
                'Genuineness',
                'Increase self-esteem and openness to experience',
                'Unconditional positive regard',
            ]),
        )
        .optional(),
    goals: z
        .array(
            z.enum([
                'Promote self-acceptance and personal growth',
                'Provide an empathic and nonjudgmental therapeutic environment',
                'Support clients in discovering their own solutions',
            ]),
        )
        .optional(),
    otherGoal: z.string().trim().optional(),
});

const petSchema = z.object({
    treatmentApproach: z.literal('PROLONGED_EXPOSURE'),
    interventions: z
        .array(z.enum(['Identify allies and assets', 'Imaginal exposure', 'In-vivo exposure']))
        .optional(),
    goals: z
        .array(
            z.enum([
                'Reduce trauma-related avoidance',
                'Decrease emotional distress associated with trauma memories',
                'Increase mastery over trauma reminders',
            ]),
        )
        .optional(),
    otherGoal: z.string().trim().optional(),
});

const psychodynamicSchema = z.object({
    treatmentApproach: z.literal('PSYCHODYNAMIC'),
    interventions: z
        .array(
            z.enum([
                'Free association',
                'Identify patterns',
                'Improving relationships',
                'Understanding emotions',
            ]),
        )
        .optional(),
    goals: z
        .array(
            z.enum([
                'Increase insight into unconscious patterns and conflicts',
                'Improve emotional functioning and relational patterns',
                'Resolve internal conflicts rooted in early experiences',
            ]),
        )
        .optional(),
    otherGoal: z.string().trim().optional(),
});

const rebtSchema = z.object({
    treatmentApproach: z.literal('REBT'),
    interventions: z
        .array(
            z.enum([
                'Cognitive disputation',
                'Disputing irrational beliefs',
                'Imagining worst case scenarios',
                'Rational emotive imagery',
                'Role-playing',
                'Stop and monitor',
                'Teaching the anatomy of emotions',
                'Using humor',
            ]),
        )
        .optional(),
    goals: z
        .array(
            z.enum([
                'Identify and challenge irrational beliefs',
                'Replace them with rational and adaptive beliefs',
                'Reduce emotional distress linked to rigid thinking',
            ]),
        )
        .optional(),
    otherGoal: z.string().trim().optional(),
});

const sfbtSchema = z.object({
    treatmentApproach: z.literal('SFBT'),
    interventions: z
        .array(
            z.enum([
                'Coping questions',
                'Goal development questions',
                'Identify small victories versus focusing on negative',
                'Looking for exceptions',
                'Looking for previous solutions',
                'Miracle question',
                'Pre-session change questions',
                'Recognize personal strength and virtues',
                'Reinforced positive qualities',
                'Scaling questions',
            ]),
        )
        .optional(),
    goals: z
        .array(
            z.enum([
                'Identify strengths, resources, and exceptions',
                'Build concrete and achievable solutions',
                'Promote rapid goal-directed change',
            ]),
        )
        .optional(),
    otherGoal: z.string().trim().optional(),
});

const somaticSchema = z.object({
    treatmentApproach: z.literal('SOMATIC'),
    interventions: z
        .array(
            z.enum([
                'Body Awareness / Interoceptive Tracking',
                'Breathwork and Vocalizations',
                'Developing Boundaries',
                'Grounding',
                'Movement',
                'Postural and Stance Exploration',
                'Resourcing',
                'Self-Soothing Touch',
                'Titration and Pendulation',
            ]),
        )
        .optional(),
    goals: z
        .array(
            z.enum([
                'Improve nervous-system regulation and emotional self-regulation',
                'Reduce trauma- and stress-related activation, including persistent fight, flight, freeze, or shutdown patterns',
                'Increase body awareness, safety, and resilience',
            ]),
        )
        .optional(),
    otherGoal: z.string().trim().optional(),
});

const spiritualSchema = z.object({
    treatmentApproach: z.literal('SPIRITUAL'),
    interventions: z
        .array(
            z.enum([
                'Aura reading',
                'Energy healing',
                'Healing with light energy',
                'Intuitive counseling',
                'Meaning-focused intervention',
                'Prayer',
                'Religious-specific intervention',
                'Science of mind techniques',
                'Spiritual healing',
                'Spiritual meditation',
            ]),
        )
        .optional(),
    goals: z
        .array(
            z.enum([
                'Support meaning-making and spiritual well-being',
                'Integrate spiritual beliefs into healing',
                'Strengthen inner peace, purpose, and alignment',
            ]),
        )
        .optional(),
    otherGoal: z.string().trim().optional(),
});

const sbtSchema = z.object({
    treatmentApproach: z.literal('STRENGTH_BASED'),
    interventions: z
        .array(
            z.enum([
                'Explore interests and hobbies',
                'Explore values and motivation',
                'Identify what keeps client motivated',
                'Identify future goals',
                'Identify strengths, skills, attributes, and knowledge',
                'Identify support network',
                'Traits you admire in others',
                'What energizes you',
            ]),
        )
        .optional(),
    goals: z
        .array(
            z.enum([
                'Identify and amplify personal strengths',
                'Build resilience and empowerment',
                'Shift focus from deficits to capabilities',
            ]),
        )
        .optional(),
    otherGoal: z.string().trim().optional(),
});

const sftSchema = z.object({
    treatmentApproach: z.literal('STRUCTURAL_FAMILY'),
    interventions: z
        .array(
            z.enum([
                'Challenging unproductive assumptions',
                'Developing structural maps',
                'Guided enactments',
                'Joining',
                'Not stepping in',
                'Reframing to illuminate family structure',
                'Unbalancing',
                'Using intensity to elicit change',
            ]),
        )
        .optional(),
    goals: z
        .array(
            z.enum([
                'Restructure family boundaries, roles, and hierarchies',
                'Improve communication and relational functioning',
                'Reduce dysfunctional interaction patterns',
            ]),
        )
        .optional(),
    otherGoal: z.string().trim().optional(),
});

const gottmanSchema = z.object({
    treatmentApproach: z.literal('GOTTMAN'),
    interventions: z
        .array(
            z.enum([
                'Emotional regulation',
                'Four Horsemen psychoeducation',
                'Individual Meta-Emotion interview',
                'Increasing fondness',
                'Interrupting harsh startups',
                'Processing old memories',
                'Repairing ruptures',
            ]),
        )
        .optional(),
    goals: z
        .array(
            z.enum([
                'Strengthen friendship, intimacy, and shared meaning',
                'Reduce conflict through evidence-based communication skills',
                'Build long-term relationship stability',
            ]),
        )
        .optional(),
    otherGoal: z.string().trim().optional(),
});

const tftSchema = z.object({
    treatmentApproach: z.literal('TRAUMA_FOCUSED'),
    interventions: z
        .array(
            z.enum(['Effective regulation', 'Psychoeducation', 'Relaxation', 'Trauma narrative']),
        )
        .optional(),
    goals: z
        .array(
            z.enum([
                'Promote safety and stabilization',
                'Process traumatic experiences in a structured manner',
                'Restore empowerment, coping, and emotional regulation',
            ]),
        )
        .optional(),
    otherGoal: z.string().trim().optional(),
});

export const treatmentApproachSchema = z.discriminatedUnion('treatmentApproach', [
    aedpSchema,
    actSchema,
    abtSchema,
    bftSchema,
    cbtSchema,
    cptSchema,
    craftSchema,
    cftSchema,
    ctSchema,
    cstSchema,
    dbtSchema,
    eftSchema,
    extSchema,
    erpSchema,
    emdrSchema,
    imagoSchema,
    ifsSchema,
    iptSchema,
    istdpSchema,
    mbctSchema,
    miSchema,
    ntSchema,
    pcitSchema,
    pctSchema,
    petSchema,
    psychodynamicSchema,
    rebtSchema,
    sfbtSchema,
    somaticSchema,
    spiritualSchema,
    sbtSchema,
    sftSchema,
    gottmanSchema,
    tftSchema,
]);

type MutuallyAssignable<A, B> = [A] extends [B] ? ([B] extends [A] ? true : never) : never;

// A branch missing from the union, or one outside TREATMENT_APPROACHES, fails the build.
const _treatmentApproachBranchesInSync: MutuallyAssignable<
    z.infer<typeof treatmentApproachSchema>['treatmentApproach'],
    TreatmentApproach
> = true;

export const interventionsSchema = z.array(treatmentApproachSchema);
